import { NAVIGATION_SOURCE_ASIDE_MENU, NAVIGATION_SOURCE_STATE_KEY } from '@aca-new/app/shared/constants/navigation-source.constants';
import {
  EQimaIqEventName,
  QIMA_IQ_TRACKING_FEATURE_NAME,
  QIMA_IQ_TRACKING_SOURCE_TYPE,
} from '@aca-new/app/shared/services/data-track-services/qima-iq-tracking/qima-iq-tracking.constants';
import { IQimaIqEventProperties, TQimaIqAccountType, TQimaIqNavigationEntry } from '@aca-new/app/shared/services/data-track-services/qima-iq-tracking/qima-iq-tracking.interface';
import { AppSystemEventService } from '@aca-new/app/shared/services/data-track-services/system-event/app-system-event.service';
import { AppAuthenticationService } from '@aca-new/app/shared/services/user-services/app-authentication/app-authentication.service';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { isPlainObject, mapKeys, mapValues, snakeCase } from 'lodash/index';
import { EMPTY, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class QimaIqTrackingService {
  private readonly _appSystemEventService = inject(AppSystemEventService);
  private readonly _appAuthenticationService = inject(AppAuthenticationService);
  private readonly _appUserSettingService = inject(AppUserSettingService);
  private readonly _router = inject(Router);

  /**
   * Sends one QIMA IQ event with the common properties. Fire-and-forget: errors never reach the caller.
   * @template TEventName - Event name, which selects the property shape.
   * @param {TEventName} eventName - Event name from the IQ catalog.
   * @param {IQimaIqEventProperties[TEventName]} properties - Event-specific properties (camelCase).
   * @returns {void}
   */
  public track<TEventName extends EQimaIqEventName>(eventName: TEventName, properties: IQimaIqEventProperties[TEventName]): void {
    const user = this._appAuthenticationService.getCurrentUser();

    if (!user?.id) {
      return;
    }

    const isLoginFromBackOffice = this._appAuthenticationService.getIsLoginFromBackOffice();
    const dataJson: Record<string, unknown> = {
      eventName,
      timestamp: new Date().toISOString(),
      userId: user.id,
      clientId: user.company?.id ?? '',
      accountType: this._getAccountType(isLoginFromBackOffice),
      internalFlag: isLoginFromBackOffice,
      sessionId: this._appAuthenticationService.getAuthenticationData().sessionId ?? '',
      page: this._getPagePath(),
      ...properties,
    };

    this._appSystemEventService
      .sendSystemEvent$({
        action: eventName,
        featureName: QIMA_IQ_TRACKING_FEATURE_NAME,
        sourceId: user.id,
        sourceType: QIMA_IQ_TRACKING_SOURCE_TYPE,
        dataJson: this._toSnakeCaseKeys(dataJson),
      })
      .pipe(catchError((): Observable<never> => EMPTY))
      .subscribe();
  }

  /**
   * Resolves how the user reached the page being activated. Call it from a routed page's init.
   * Falls back to the last successful navigation when the page initializes after the navigation has completed.
   * @returns {TQimaIqNavigationEntry} `direct` for the app's first navigation, `menu` for a side-menu click, else `in_app`.
   */
  public getNavigationEntry(): TQimaIqNavigationEntry {
    const navigation = this._router.getCurrentNavigation() ?? this._router.lastSuccessfulNavigation;

    if (!navigation?.previousNavigation) {
      return 'direct';
    }

    const isFromAsideMenu = navigation.trigger === 'imperative' && navigation.extras.state?.[NAVIGATION_SOURCE_STATE_KEY] === NAVIGATION_SOURCE_ASIDE_MENU;

    return isFromAsideMenu ? 'menu' : 'in_app';
  }

  /**
   * @param {Record<string, unknown>} record - Event payload in camelCase.
   * @returns {Record<string, unknown>} Same payload with snake_case keys, including nested objects and objects inside arrays.
   */
  private _toSnakeCaseKeys(record: Record<string, unknown>): Record<string, unknown> {
    const snakeCaseRecord = mapKeys(record, (_value: unknown, key: string): string => snakeCase(key));

    return mapValues(snakeCaseRecord, (value: unknown): unknown => this._toSnakeCaseValue(value));
  }

  /**
   * @param {unknown} value - Payload value in camelCase.
   * @returns {unknown} Plain objects (also inside arrays) with snake_case keys; other values unchanged.
   */
  private _toSnakeCaseValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item: unknown): unknown => this._toSnakeCaseValue(item));
    }

    return isPlainObject(value) ? this._toSnakeCaseKeys(value as Record<string, unknown>) : value;
  }

  /**
   * @param {boolean} isLoginFromBackOffice - True for a back-office (SSO) session.
   * @returns {TQimaIqAccountType} Account type reported to analytics.
   */
  private _getAccountType(isLoginFromBackOffice: boolean): TQimaIqAccountType {
    if (isLoginFromBackOffice) {
      return 'internal';
    }

    return this._appUserSettingService.isSubAccount() ? 'sub' : 'master';
  }

  /**
   * The router URL still points at the previous page while a navigation activates, so the target URL wins.
   * @returns {string} Route path without query string or fragment.
   */
  private _getPagePath(): string {
    const targetUrl = this._router.getCurrentNavigation()?.finalUrl;
    const url = targetUrl ? this._router.serializeUrl(targetUrl) : this._router.url;

    return url.split(/[?#]/)[0] ?? '';
  }
}
